// File: apps/fe/src/shared/components/video-player/components/video-player.tsx
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import { Skeleton } from '@mui/material';
import { PlayerProvider } from '../context/player-context';
import { PlaybackConfig, PlayerConfig, ProgressPayload } from '../types/player';
import { getVideoElementFromMux, getVideoAspectRatio } from '../utils/media-utils';

// Import components
import { VideoContainer } from './core/video-container';
import { PlayerControls } from './core/player-controls';
import { StoryboardPreview } from './features/storyboard-preview';
import { LoopControls } from './features/loop-controls';
import { LoopModal } from './features/loop-modal';
import { EndScreen } from './features/end-screen';
import { ActionOverlay } from './overlay/action-overlay';
import { BufferingSpinner } from './overlay/buffering-spinner';
import {
  useVideoPlayerControl,
  useVideoPlayerRegistration,
  useVideoPlayerStore,
} from '../hooks/integration/use-video-player-control';
import { MuxPlayerError } from './video-player-error';

export interface VideoPlayerProps extends PlayerConfig, PlaybackConfig {
  isLastVideo?: boolean;
  onNextVideo?: () => void;
  onHeightChange?: (height: number) => void;
  playerId?: string;
  endScreen?: boolean;
  startTime?: number;
  onProgressSave?: (progress: ProgressPayload) => void;
  simpleControls?: boolean;
  handleAspectRatio?: boolean; // New prop to toggle aspect ratio handling
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  playbackId,
  playbackToken,
  videoId,
  videoTitle,
  storyboardMetadataUrl,
  isLastVideo = false,
  onNextVideo,
  onHeightChange,
  muxMetadata,
  autoPlay = true,
  lessonDocumentId,
  startTime = 0,
  playerId = 'default', // Default player ID
  endScreen = false,
  onProgressSave,
  simpleControls = false,
  handleAspectRatio = false, // Default to false to maintain previous behavior
}) => {
  // Track if player is loaded
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [playerError, setPlayerError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const muxPlayerRef = useRef<any>(null);
  const [playerHeight, setPlayerHeight] = useState(0);
  const [playerWidth, setPlayerWidth] = useState(0); // Add state for width
  const videoElementRef = useRef<HTMLMediaElement | null>(null);
  const { setVideoDuration } = useVideoPlayerStore();
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [autoPlayFailed, setAutoplayFailed] = useState(false);

  // Handle height and width changes for responsive layout
  useLayoutEffect(() => {
    if (muxPlayerRef.current) {
      const rect = muxPlayerRef.current.getBoundingClientRect();
      const height = rect.height;
      const width = rect.width;

      if (height > 0) {
        setPlayerHeight(height);

        if (onHeightChange) {
          onHeightChange(height);
        }
      }

      if (width > 0) {
        setPlayerWidth(width);
      }
    }
  }, [onHeightChange, muxPlayerRef, playerId]);

  // Handle window resize to update dimensions
  useEffect(() => {
    const handleResize = () => {
      if (muxPlayerRef.current) {
        const rect = muxPlayerRef.current.getBoundingClientRect();
        const height = rect.height;
        const width = rect.width;

        if (height > 0) {
          setPlayerHeight(height);

          if (onHeightChange) {
            onHeightChange(height);
          }
        }

        if (width > 0) {
          setPlayerWidth(width);
        }
      }
    };

    // Get initial dimensions from container if MuxPlayer isn't loaded yet
    if (!muxPlayerRef.current && containerRef.current) {
      const rect = containerRef.current?.getBoundingClientRect();
      const height = rect.height;
      const width = rect.width;

      if (height > 0) {
        setPlayerHeight(height);
      }

      if (width > 0) {
        setPlayerWidth(width);
      }
    }

    window.addEventListener('resize', handleResize);

    // Call once on mount to ensure we have dimensions
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [onHeightChange, muxPlayerRef, containerRef, playerId]);

  // Make sure the video element is accessible to our hooks
  useEffect(() => {
    if (muxPlayerRef.current) {
      const height = muxPlayerRef.current.getBoundingClientRect().height;
      if (height > 0) {
        setPlayerHeight(height);
      }

      // Store ref globally for debugging
      (window as any).muxPlayer = muxPlayerRef.current;

      // Setup an interval to check for the video element and log it for debugging
      const checkVideoElement = setInterval(() => {
        const videoEl = getVideoElementFromMux();
        if (videoEl) {
          videoElementRef.current = videoEl;

          // Update the duration in the store when available
          if (Number.isFinite(videoEl.duration) && videoEl.duration > 0) {
            setVideoDuration(playerId, videoEl.duration);
          }

          // Attempt autoplay if enabled
          if (autoPlay) {
            videoEl
              .play()
              .then(() => {})
              .catch(() => {
                setAutoplayFailed(true);
              });
          }

          // We can clear the interval once found
          clearInterval(checkVideoElement);
        }
      }, 500);

      // Clean up interval on unmount
      return () => {
        clearInterval(checkVideoElement);
      };
    }
  }, [muxPlayerRef.current, playerId, setVideoDuration, autoPlay, setPlayerHeight]);

  // Register this player with the global store
  useVideoPlayerRegistration(playerId, videoElementRef.current, playerHeight, playerWidth);

  // Log when player is loaded
  useEffect(() => {
    if (playerLoaded) {
    }
  }, [playerLoaded]);

  // Update player height on metadata loaded for more reliability
  useEffect(() => {
    if (playerLoaded && muxPlayerRef.current) {
      const height = muxPlayerRef.current.getBoundingClientRect().height;
      if (height > 0) {
        setPlayerHeight(height);
      }
    }
  }, [playerLoaded, muxPlayerRef, setPlayerHeight]);

  // Setup additional event listeners on the MuxPlayer
  useEffect(() => {
    const muxPlayerElement = muxPlayerRef.current;
    if (!muxPlayerElement) return;

    const handlePlay = () => {};

    const handlePause = () => {};

    const handleEnded = () => {};

    const handleDurationChange = () => {
      if (muxPlayerElement.duration && Number.isFinite(muxPlayerElement.duration)) {
        setVideoDuration(playerId, muxPlayerElement.duration);
      }
    };

    // Try to attach event listeners directly to the MuxPlayer element
    try {
      muxPlayerElement.addEventListener('play', handlePlay);
      muxPlayerElement.addEventListener('pause', handlePause);
      muxPlayerElement.addEventListener('ended', handleEnded);
      muxPlayerElement.addEventListener('durationchange', handleDurationChange);

      return () => {
        muxPlayerElement.removeEventListener('play', handlePlay);
        muxPlayerElement.removeEventListener('pause', handlePause);
        muxPlayerElement.removeEventListener('ended', handleEnded);
        muxPlayerElement.removeEventListener('durationchange', handleDurationChange);
      };
    } catch (err) {}
  }, [muxPlayerRef.current, playerId, setVideoDuration]);

  // Add effect to detect video dimensions - but only if handleAspectRatio is true
  useEffect(() => {
    if (!handleAspectRatio) return; // Skip aspect ratio detection if not handling it

    const detectVideoDimensions = () => {
      if (!videoElementRef.current) return;

      // Cast to HTMLVideoElement to access videoWidth and videoHeight properties
      const videoEl = videoElementRef.current as HTMLVideoElement;

      // Use the utility function to get the aspect ratio
      const ratio = getVideoAspectRatio(videoEl);
      if (ratio !== null) {
        setAspectRatio(ratio);
      }
    };

    // Attempt to detect dimensions when video element is available
    if (videoElementRef.current) {
      detectVideoDimensions();

      // Add loadedmetadata event listener to detect dimensions when they become available
      const videoEl = videoElementRef.current;
      videoEl.addEventListener('loadedmetadata', detectVideoDimensions);

      return () => {
        videoEl.removeEventListener('loadedmetadata', detectVideoDimensions);
      };
    }
  }, [videoElementRef.current, handleAspectRatio]);

  if (playerError) {
    return <MuxPlayerError />;
  }
  return (
    <PlayerProvider
      autoPlay={autoPlay}
      lessonDocumentId={lessonDocumentId}
      playbackId={playbackId}
      playbackToken={playbackToken}
      videoId={videoId}
      videoTitle={videoTitle}
      storyboardMetadataUrl={storyboardMetadataUrl}
      muxMetadata={muxMetadata}
      onNextVideo={onNextVideo}
      endScreen={endScreen}
      onProgressSave={onProgressSave}
      simpleControls={simpleControls}
      aspectRatio={handleAspectRatio ? aspectRatio : null}
      handleAspectRatio={handleAspectRatio}
    >
      <VideoContainer>
        {/* Loading skeleton */}
        {!playbackToken || startTime === undefined || startTime === null ? (
          <div className="relative w-full h-full bg-black">
            <Skeleton
              animation="wave"
              variant="rounded"
              height="100%"
              width="100%"
              className="rounded-xl"
              ref={containerRef}
            />
          </div>
        ) : (
          /* MuxPlayer component */
          <MuxPlayer
            ref={muxPlayerRef}
            playbackId={playbackId}
            envKey={process.env.MUX_ENV_KEY}
            metadata={{
              video_id: videoId,
              video_title: videoTitle,
              viewer_user_id: '',
              ...muxMetadata,
            }}
            startTime={startTime}
            tokens={{
              playback: playbackToken,
            }}
            preload="auto"
            muted={false}
            streamType="on-demand"
            x-webkit-airplay="allow"
            style={
              {
                '--controls': 'none',
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                objectFit: 'contain',
                maxHeight: '100%',
                zIndex: 2,
                display: 'block',
                backgroundColor: 'black',
              } as React.CSSProperties
            }
            defaultHiddenCaptions
            onLoadedMetadata={() => {
              setPlayerLoaded(true);

              // Try to get duration from MuxPlayer directly
              if (muxPlayerRef.current && muxPlayerRef.current.duration) {
                const duration = muxPlayerRef.current.duration;
                if (Number.isFinite(duration) && duration > 0) {
                  setVideoDuration(playerId, duration);
                }
              }

              // Set a timeout to ensure the video element is available after metadata is loaded
              // Only check for aspect ratio if handleAspectRatio is true
              if (handleAspectRatio) {
                setTimeout(() => {
                  const videoEl = getVideoElementFromMux();
                  if (videoEl) {
                    videoElementRef.current = videoEl;
                    const ratio = getVideoAspectRatio(videoEl as HTMLVideoElement);
                    if (ratio !== null) {
                      setAspectRatio(ratio);
                    }
                  }
                }, 100);
              }
            }}
            onDurationChange={(e: any) => {
              // The event might have duration information
              if (e && e.target && Number.isFinite(e.target.duration)) {
                setVideoDuration(playerId, e.target.duration);
              }
            }}
            onPlaying={() => {}}
            onPause={() => {}}
            onEnded={() => {}}
            onError={(e) => {
              setPlayerError(true);
            }}
          />
        )}

        {/* Controls background gradient */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent to-black/50" />

        {/* Buffering spinner overlay */}
        <BufferingSpinner />

        {/* Action overlays for player state */}
        <ActionOverlay />

        {/* End screen with buttons */}
        {endScreen && <EndScreen isLastVideo={isLastVideo} />}

        {/* Loop indicator */}
        <LoopControls />

        {/* Player controls */}
        <>
          <PlayerControls autoPlayFailed={autoPlayFailed} muxPlayerRef={muxPlayerRef} />
          <StoryboardPreview />
        </>

        {/* Loop settings modal */}
        <LoopModal />
      </VideoContainer>
    </PlayerProvider>
  );
};

// Also export the hook for external control
export { useVideoPlayerControl };

export default VideoPlayer;
